/** @jsxImportSource @emotion/react */

import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@material-ui/core'
import { toast } from 'react-toastify'
import PageTitle from '../../components/PageTitle'
import Seo from '../../components/Seo'
import { useAuthContext } from '../../context/auth.context'
import { GET_COMPANIES_NAMES } from '../../graphql/company.graphql'
import { GET_PROJECTS } from '../../graphql/project.graphql'
import { GET_CATEGORIES } from '../../graphql/category.graphql'
import {
  ADDITIONAL_TIME_REQUEST_CREATE,
  ADDITIONAL_TIME_REQUEST_DECIDE,
  GET_ADDITIONAL_TIME_REQUESTS,
  GET_MY_ADDITIONAL_TIME_REQUESTS,
} from '../../graphql/additionalTimeRequest.graphql'

const month = () => new Date().toISOString().slice(0, 7)

const formatDate = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pl-PL')
  } catch (error) {
    return '—'
  }
}

const AdditionalTimeRequests = () => {
  const { userRole, userId } = useAuthContext()
  const decisionMaker = Number(userRole) >= 4
  const [form, setForm] = useState({ company: '', project: '', category: '', month: month(), timezone: 'Europe/Warsaw', requestedMinutes: '', reason: '' })
  const companies = useQuery(GET_COMPANIES_NAMES).data?.getCompanies || []
  const projects = useQuery(GET_PROJECTS).data?.getProjects || []
  const categories = useQuery(GET_CATEGORIES).data?.getCategories || []
  const visibleProjects = useMemo(() => projects.filter((project) => !form.company || project.company?._id === form.company), [projects, form.company])
  const requestQuery = decisionMaker ? GET_ADDITIONAL_TIME_REQUESTS : GET_MY_ADDITIONAL_TIME_REQUESTS
  const requests = useQuery(requestQuery).data?.[decisionMaker ? 'getAdditionalTimeRequests' : 'getMyAdditionalTimeRequests'] || []
  const [createRequest, { loading }] = useMutation(ADDITIONAL_TIME_REQUEST_CREATE, { refetchQueries: [{ query: GET_MY_ADDITIONAL_TIME_REQUESTS }] })
  const [decideRequest] = useMutation(ADDITIONAL_TIME_REQUEST_DECIDE, { refetchQueries: [{ query: GET_ADDITIONAL_TIME_REQUESTS }] })
  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value })
  const submit = async (event) => {
    event.preventDefault()
    try {
      await createRequest({ variables: { input: { ...form, requestedMinutes: Number(form.requestedMinutes), idempotencyKey: `additional-time:${userId}:${Date.now()}` } } })
      toast.success('Prośba o dodatkowy czas została wysłana')
      setForm({ ...form, requestedMinutes: '', reason: '' })
    } catch (error) { toast.error(error.message) }
  }
  const decide = async (request, decision) => {
    const grantedMinutes = decision === 'APPROVED' ? Number(window.prompt('Przyznane minuty', request.requestedMinutes)) : null
    const reason = window.prompt('Powód decyzji')
    if (!reason || (decision === 'APPROVED' && (!grantedMinutes || grantedMinutes < 1))) return
    try {
      await decideRequest({ variables: { id: request._id, input: { decision, grantedMinutes, reason, decisionKey: `additional-decision:${request._id}:${Date.now()}` } } })
      toast.success('Decyzja zapisana')
    } catch (error) { toast.error(error.message) }
  }

  return <>
    <Seo title='Additional time requests' />
    <PageTitle><h1>Prośby o dodatkowy czas</h1></PageTitle>
    {!decisionMaker && <Paper css={panel}>
      <form onSubmit={submit} css={formStyle}>
        <FormControl><InputLabel>Firma</InputLabel><Select value={form.company} onChange={update('company')} required>{companies.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Projekt</InputLabel><Select value={form.project} onChange={update('project')} required>{visibleProjects.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <FormControl><InputLabel>Kategoria</InputLabel><Select value={form.category} onChange={update('category')} required>{categories.filter((item) => !item.isHidden).map((item) => <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>)}</Select></FormControl>
        <TextField label='Miesiąc' type='month' value={form.month} onChange={update('month')} required InputLabelProps={{ shrink: true }} />
        <TextField label='Minuty' type='number' value={form.requestedMinutes} onChange={update('requestedMinutes')} required inputProps={{ min: 1 }} />
        <TextField label='Uzasadnienie' value={form.reason} onChange={update('reason')} required multiline />
        <Button type='submit' variant='contained' color='primary' disabled={loading}>Wyślij prośbę</Button>
      </form>
    </Paper>}
    <Paper css={panel}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Wysłano</TableCell>
            <TableCell>Decyzja</TableCell>
            <TableCell>Ścieżka</TableCell>
            <TableCell>Minuty</TableCell>
            <TableCell>Uzasadnienie prośby</TableCell>
            <TableCell>Uzasadnienie decyzji</TableCell>
            <TableCell>Akcja</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request._id}>
              <TableCell>{request.status}</TableCell>
              <TableCell>{formatDate(request.createdAt)}</TableCell>
              <TableCell>{formatDate(request.decidedAt)}</TableCell>
              <TableCell>{request.company?.name} / {request.project?.name} / {request.category?.name}</TableCell>
              <TableCell>{request.requestedMinutes}{request.grantedMinutes != null ? ` / ${request.grantedMinutes}` : ''}</TableCell>
              <TableCell>{request.reason}</TableCell>
              <TableCell>{request.decisionReason || '—'}</TableCell>
              <TableCell>
                {decisionMaker && request.status === 'PENDING' && <>
                  <Button onClick={() => decide(request, 'APPROVED')} color='primary'>Akceptuj</Button>
                  <Button onClick={() => decide(request, 'REJECTED')} color='secondary'>Odrzuć</Button>
                </>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {requests.length === 0 && <div css={empty}>Brak próśb.</div>}
    </Paper>
  </>
}

const panel = { marginBottom: '1rem', padding: '1rem', overflowX: 'auto' }
const formStyle = { display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', alignItems: 'center' }
const empty = { padding: '1rem', color: '#666' }

export default AdditionalTimeRequests
